import FormOnlyStepContainer from 'components/step-flow/FormOnlyStep';
import {
  getEquipmentFormElements,
  GPS_TYPES,
} from 'components/create-trial/EquipmentInformationForm';
import { StepProps } from '../../types/stepFlow.type';
import { useAppSelector } from '../../redux/hooks';
import * as Yup from 'yup';

interface EquipmentInformationContainerProps extends StepProps {
  mode: 'execution' | 'harvester';
}

// Validation schema for execution equipment
export const executionEquipmentSchema = Yup.object({
  execution_equipment_swath_width: Yup.number()
    .required('Swath width is required.')
    .min(0.01, 'Swath width must be a positive number.'),
  execution_equipment_swath_width_units: Yup.string()
    .required('Swath width units are required.')
    .oneOf(['m', 'ft'], 'Units must be m or ft.'),
  execution_equipment_rate_change_distance: Yup.number()
    .required('Rate change distance is required.')
    .min(0.01, 'Rate change distance must be a positive number.'),
  execution_equipment_rate_change_distance_units: Yup.string()
    .required('Rate change distance units are required.')
    .oneOf(['m', 'ft'], 'Units must be m or ft.'),
  execution_equipment_can_split_swath: Yup.boolean().required(),
  execution_equipment_dgps_type: Yup.string().required('GPS type is required.'),
  execution_equipment_dgps_accuracy: Yup.number()
    .required('GPS accuracy is required.')
    .min(0.01, 'GPS accuracy must be a positive number.'),
  execution_equipment_dgps_units: Yup.string()
    .required('GPS accuracy units are required.')
    .oneOf(['m', 'cm', 'ft', 'in'], 'Units must be m, cm, ft, or in.'),
  treatment_drift_distance: Yup.number()
    .required('Treatment drift distance is required.')
    .min(0, 'Treatment drift distance must be 0 or positive.'),
  treatment_drift_units: Yup.string()
    .required('Treatment drift units are required.')
    .oneOf(['m', 'ft'], 'Units must be m or ft.'),
});

// Validation schema for harvester
export const harvesterSchema = Yup.object({
  harvester_swath_width: Yup.number()
    .required('Swath width is required.')
    .min(0.01, 'Swath width must be a positive number.'),
  harvester_swath_width_units: Yup.string()
    .required('Swath width units are required.')
    .oneOf(['m', 'ft'], 'Units must be m or ft.'),
  harvester_spatial_resolution: Yup.number()
    .required('Spatial resolution is required.')
    .min(0.01, 'Spatial resolution must be a positive number.'),
  harvester_spatial_resolution_units: Yup.string()
    .required('Spatial resolution units are required.')
    .oneOf(['m', 'ft'], 'Units must be m or ft.'),
  harvester_dgps_type: Yup.string().required('GPS type is required.'),
  harvester_dgps_accuracy: Yup.number()
    .required('GPS accuracy is required.')
    .min(0.01, 'GPS accuracy must be a positive number.'),
  harvester_dgps_units: Yup.string()
    .required('GPS accuracy units are required.')
    .oneOf(['m', 'cm', 'ft', 'in'], 'Units must be m, cm, ft, or in.'),
  max_likely_harvest_angle: Yup.number()
    .required('Maximum harvest angle is required.')
    .min(0, 'Harvest angle must be 0 or greater.')
    .max(360, 'Harvest angle must be 360 or less.'),
});

const EquipmentInformationContainer = ({
  children,
  mode,
}: EquipmentInformationContainerProps) => {
  const isExecution = mode === 'execution';

  // Get equipment type from the Trial Type step
  const trialData = useAppSelector((state) => state.createTrial.trial);
  const equipmentType = trialData.trial_equipment || 'Equipment';

  // Determine default rate change distance based on equipment type
  const getDefaultRateChangeDistance = () => {
    if (equipmentType && equipmentType.toLowerCase().includes('planter')) {
      return '25';
    }
    return '50';
  };

  // Handle form value changes for GPS auto-mapping
  const handleValuesChange = (values: Record<string, any>) => {
    const dgpsTypeField = isExecution
      ? 'execution_equipment_dgps_type'
      : 'harvester_dgps_type';
    const dgpsAccuracyField = isExecution
      ? 'execution_equipment_dgps_accuracy'
      : 'harvester_dgps_accuracy';
    const dgpsUnitsField = isExecution
      ? 'execution_equipment_dgps_units'
      : 'harvester_dgps_units';

    const gpsType = values[dgpsTypeField];
    const hasOtherValue = values[`${dgpsTypeField}-other`]; // Check if "Other" is being used

    if (gpsType && GPS_TYPES[gpsType] && !hasOtherValue) {
      const gpsConfig = GPS_TYPES[gpsType];
      if (
        values[dgpsAccuracyField] !== gpsConfig.accuracy ||
        values[dgpsUnitsField] !== gpsConfig.units
      ) {
        values[dgpsAccuracyField] = gpsConfig.accuracy;
        values[dgpsUnitsField] = gpsConfig.units;
      }
    }
  };

  const executionInitialValues = {
    execution_equipment_swath_width: '',
    execution_equipment_swath_width_units: 'ft',
    execution_equipment_rate_change_distance: getDefaultRateChangeDistance(),
    execution_equipment_rate_change_distance_units: 'ft',
    execution_equipment_can_split_swath: false,
    execution_equipment_dgps_type: 'WAAS',
    execution_equipment_dgps_accuracy: 2,
    execution_equipment_dgps_units: 'm',
    treatment_drift_distance: '0',
    treatment_drift_units: 'ft',
  };

  const harvesterInitialValues = {
    harvester_swath_width: '',
    harvester_swath_width_units: 'ft',
    harvester_spatial_resolution: '50',
    harvester_spatial_resolution_units: 'ft',
    harvester_dgps_type: 'WAAS',
    harvester_dgps_accuracy: 2,
    harvester_dgps_units: 'm',
    max_likely_harvest_angle: '0',
  };

  return (
    <FormOnlyStepContainer
      uniqueKey={isExecution ? 'execution-equipment' : 'harvester-equipment'}
      initialValues={
        isExecution ? executionInitialValues : harvesterInitialValues
      }
      formElements={getEquipmentFormElements(mode, equipmentType)}
      validationSchema={
        isExecution ? executionEquipmentSchema : harvesterSchema
      }
      valuesCallback={handleValuesChange}
    >
      {children}
    </FormOnlyStepContainer>
  );
};

export default EquipmentInformationContainer;
